import { RascunhoEmpreendimento, RascunhoResumo } from "../types/rascunhoEmpreendimento";

// Rascunhos do wizard de "Novo Empreendimento", em localStorage (não
// sessionStorage — precisa sobreviver a fechar o navegador e voltar dias
// depois, diferente do acompanhamento de job da IA em iaImportService.ts).
//
// Um índice leve (lista de {id, nome, atualizadoEm}) fica numa chave só,
// pra listar sem reidratar o rascunho inteiro de cada um; o conteúdo
// completo de cada rascunho fica numa chave própria.
const PREFIXO = "tactimobi:empreendimentos:";
const CHAVE_INDICE = `${PREFIXO}rascunhos-index`;
const chaveRascunho = (id: string) => `${PREFIXO}rascunho:${id}`;

// Rascunho não tocado há mais de 7 dias é descartado silenciosamente na
// leitura — evita acumular lixo de tentativas abandonadas há meses.
const EXPIRACAO_MS = 7 * 24 * 60 * 60 * 1000;

const gerarId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback só pra navegador muito antigo/contexto não-seguro sem crypto.randomUUID.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const lerIndice = (): RascunhoResumo[] => {
  try {
    const bruto = localStorage.getItem(CHAVE_INDICE);
    return bruto ? JSON.parse(bruto) : [];
  } catch {
    return [];
  }
};

const gravarIndice = (indice: RascunhoResumo[]): void => {
  try {
    localStorage.setItem(CHAVE_INDICE, JSON.stringify(indice));
  } catch (err) {
    // Quota estourada ou storage indisponível (modo privado etc.) — o
    // autosave simplesmente para de funcionar silenciosamente, não é
    // motivo pra quebrar o resto da tela.
    console.error("Erro ao gravar índice de rascunhos:", err);
  }
};

export const rascunhoEmpreendimentoService = {
  gerarId,

  /** Lista os rascunhos válidos (não expirados), mais recente primeiro. Remove os expirados do storage como efeito colateral. */
  listar: (): RascunhoResumo[] => {
    const indice = lerIndice();
    const agora = Date.now();
    const validos: RascunhoResumo[] = [];
    let mudou = false;

    for (const item of indice) {
      const idade = agora - new Date(item.atualizadoEm).getTime();
      if (Number.isNaN(idade) || idade > EXPIRACAO_MS) {
        try {
          localStorage.removeItem(chaveRascunho(item.id));
        } catch {
          // best-effort
        }
        mudou = true;
      } else {
        validos.push(item);
      }
    }

    if (mudou) gravarIndice(validos);
    return validos.sort((a, b) => b.atualizadoEm.localeCompare(a.atualizadoEm));
  },

  obter: (id: string): RascunhoEmpreendimento | null => {
    try {
      const bruto = localStorage.getItem(chaveRascunho(id));
      return bruto ? JSON.parse(bruto) : null;
    } catch {
      return null;
    }
  },

  salvar: (rascunho: RascunhoEmpreendimento): void => {
    try {
      localStorage.setItem(chaveRascunho(rascunho.id), JSON.stringify(rascunho));
    } catch (err) {
      console.error("Erro ao gravar rascunho:", err);
      return;
    }
    const indice = lerIndice().filter((r) => r.id !== rascunho.id);
    indice.push({ id: rascunho.id, nome: rascunho.nome, atualizadoEm: rascunho.atualizadoEm });
    gravarIndice(indice);
  },

  remover: (id: string): void => {
    try {
      localStorage.removeItem(chaveRascunho(id));
    } catch {
      // best-effort
    }
    gravarIndice(lerIndice().filter((r) => r.id !== id));
  },
};
